import { createContext, useContext, useState, useEffect } from 'react';

type Language = 'en' | 'ar';

type LanguageContextType = {
  language: Language;
  toggleLanguage: () => void;
  t: (key: string) => string;
};

const translations = {
  en: {
    // Auth
    login: 'Login',
    register: 'Register',
    email: 'Email',
    phone: 'Phone',
    password: 'Password',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    logout: 'Logout',
    // Navigation
    dashboard: 'Dashboard',
    kyc: 'KYC Verification',
    transactions: 'Transactions',
    chat: 'Chat Support',
    settings: 'Settings',
    // Dashboard
    balance: 'Balance',
    deposit: 'Deposit',
    withdraw: 'Withdraw',
    history: 'History',
    // KYC
    kycStatus: 'KYC Status',
    uploadDocuments: 'Upload Documents',
    idCard: 'ID Card',
    selfie: 'Selfie',
    addressProof: 'Address Proof',
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
    // Transactions
    amount: 'Amount',
    type: 'Type',
    status: 'Status',
    date: 'Date',
    depositRequest: 'Deposit Request',
    withdrawalRequest: 'Withdrawal Request',
    // Admin
    users: 'Users',
    allTransactions: 'All Transactions',
    kycReview: 'KYC Review',
    addBalance: 'Add Balance',
    removeBalance: 'Remove Balance',
    approve: 'Approve',
    reject: 'Reject',
    // Chat
    sendMessage: 'Send Message',
    typeMessage: 'Type your message...',

    // Currency
    currency: 'Currency',
    selectCurrency: 'Select Currency',
    searchCurrency: 'Search currency...',
  },
  ar: {
    // Auth
    login: 'تسجيل الدخول',
    register: 'تسجيل',
    email: 'البريد الإلكتروني',
    phone: 'الهاتف',
    password: 'كلمة المرور',
    signIn: 'تسجيل الدخول',
    signUp: 'إنشاء حساب',
    logout: 'تسجيل الخروج',
    // Navigation
    dashboard: 'لوحة التحكم',
    kyc: 'التحقق من الهوية',
    transactions: 'المعاملات',
    chat: 'دعم الدردشة',
    settings: 'الإعدادات',
    // Dashboard
    balance: 'الرصيد',
    deposit: 'إيداع',
    withdraw: 'سحب',
    history: 'السجل',
    // KYC
    kycStatus: 'حالة التحقق',
    uploadDocuments: 'رفع المستندات',
    idCard: 'بطاقة الهوية',
    selfie: 'صورة شخصية',
    addressProof: 'إثبات العنوان',
    pending: 'قيد الانتظار',
    approved: 'موافق عليه',
    rejected: 'مرفوض',
    // Transactions
    amount: 'المبلغ',
    type: 'النوع',
    status: 'الحالة',
    date: 'التاريخ',
    depositRequest: 'طلب إيداع',
    withdrawalRequest: 'طلب سحب',
    // Admin
    users: 'المستخدمون',
    allTransactions: 'جميع المعاملات',
    kycReview: 'مراجعة التحقق',
    addBalance: 'إضافة رصيد',
    removeBalance: 'إزالة رصيد',
    approve: 'موافقة',
    reject: 'رفض',
    // Chat
    sendMessage: 'إرسال رسالة',
    typeMessage: 'اكتب رسالتك...',

    // Currency
    currency: 'العملة',
    selectCurrency: 'اختر العملة',
    searchCurrency: 'ابحث عن العملة...',
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>('en');

  useEffect(() => {
    // Set document direction based on language
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === 'en' ? 'ar' : 'en'));
  };

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations.en] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}